import type { UmbTreeItemModel } from '../../types.js';
import { UmbTreeViewElementBase } from '../tree-view-element-base.js';
import type { ManifestTreeViewTableKind, MetaTreeViewTableKindColumn } from './types.js';
import { UmbTableTreeViewRowController } from './table-tree-view-row.controller.js';
import {
	css,
	customElement,
	html,
	nothing,
	property,
	state,
	type PropertyValues,
	type TemplateResult,
} from '@umbraco-cms/backoffice/external/lit';
import { UmbTextStyles } from '@umbraco-cms/backoffice/style';
import { getItemFallbackIcon } from '@umbraco-cms/backoffice/entity-item';
import type { UmbItemDataResolver } from '@umbraco-cms/backoffice/entity-item';
import type { UmbObserverController } from '@umbraco-cms/backoffice/observable-api';
import type {
	UmbTableColumn,
	UmbTableConfig,
	UmbTableDeselectedEvent,
	UmbTableItem,
	UmbTableSelectedEvent,
} from '@umbraco-cms/backoffice/components';

import './tree-name-table-column-layout.element.js';
import '@umbraco-cms/backoffice/entity-action';

// The resolver context is keyed by item and lives for as long as the item is shown. It is created up
// front (not in the row-render callback) so the resolved name/icon are present when the row is first
// built, rather than applied as a post-render mutation.
type UmbTableTreeViewResolverContext = {
	resolver: UmbItemDataResolver;
	nameObserver: UmbObserverController<string | undefined>;
	iconObserver: UmbObserverController<string | undefined>;
	currentName?: string;
	currentIcon?: string | null;
};

/**
 * Renders the items of the tree as rows of a table, with a column for the name, any columns declared by the
 * `treeView` manifest, and the entity actions.
 * @augments {UmbTreeViewElementBase<UmbTreeItemModel>}
 */
@customElement('umb-table-tree-view')
export class UmbTableTreeViewElement extends UmbTreeViewElementBase<UmbTreeItemModel> {
	private _items: Array<UmbTreeItemModel> = [];

	@state()
	private _hideTreeItemActions = false;

	@state()
	private _tableRows: Array<UmbTableItem> = [];

	#manifest?: ManifestTreeViewTableKind;

	@property({ attribute: false })
	set manifest(value: ManifestTreeViewTableKind | undefined) {
		this.#manifest = value;
		this.#createTableRows();
	}
	get manifest(): ManifestTreeViewTableKind | undefined {
		return this.#manifest;
	}

	#tableConfig: UmbTableConfig = { allowSelection: false, allowSelectAll: false, selectOnly: false };

	get #manifestColumns(): Array<MetaTreeViewTableKindColumn> {
		return this.#manifest?.meta?.columns ?? [];
	}

	#itemMap = new Map<string, UmbTreeItemModel>();
	#rows = new Map<string, UmbTableTreeViewRowController>();
	#resolverContexts = new Map<string, UmbTableTreeViewResolverContext>();

	#onRowRendered = (element: HTMLElement | undefined, item: UmbTableItem) => {
		if (!element) {
			this.#rows.get(item.id)?.destroy();
			this.#rows.delete(item.id);
			return;
		}

		const existing = this.#rows.get(item.id);
		if (existing) {
			existing.setItem(item.entityType, item.id, this.#itemMap.get(item.id));
			return;
		}

		const row = new UmbTableTreeViewRowController(element, item.entityType, item.id, this.#itemMap.get(item.id));

		// Register before observers so synchronous emissions see the row.
		this.#rows.set(item.id, row);

		row.observeApi({
			onNoAccessChange: () => this.#updateRowSelectable(item.id),
			onPathChange: () => {
				const updated = this.#updateRowHref(item.id);
				if (updated) this._tableRows = updated;
			},
			onActiveChange: () => this.#updateRowActive(item.id),
		});
	};

	// Creates/refreshes the per-item resolvers (when the repository provides them) before the rows are
	// built, and disposes resolvers for items that are no longer shown.
	#syncResolverContexts() {
		const repository = this._treeContext?.getRepository();

		for (const item of this._items) {
			const existing = this.#resolverContexts.get(item.unique);
			if (existing) {
				existing.resolver.setData(item);
				continue;
			}

			const resolver = repository?.createTreeItemDataResolver?.(this, { entityType: item.entityType });
			if (!resolver) continue;

			const ctx: UmbTableTreeViewResolverContext = {
				resolver,
				nameObserver: undefined!,
				iconObserver: undefined!,
			};

			// Register before observers so synchronous emissions see the context.
			this.#resolverContexts.set(item.unique, ctx);
			resolver.setData(item);

			ctx.nameObserver = this.observe(
				resolver.name,
				(name) => {
					ctx.currentName = name;
					this.#updateRowName(item.unique);
				},
				`_observeName_${item.unique}`,
			);

			ctx.iconObserver = this.observe(
				resolver.icon,
				(icon) => {
					ctx.currentIcon = icon;
					this.#updateRowIcon(item.unique);
				},
				`_observeIcon_${item.unique}`,
			);
		}

		const currentIds = new Set(this._items.map((item) => item.unique));
		for (const [id, ctx] of this.#resolverContexts) {
			if (!currentIds.has(id)) {
				this.#destroyResolverContext(ctx);
				this.#resolverContexts.delete(id);
			}
		}
	}

	#destroyResolverContext(ctx: UmbTableTreeViewResolverContext) {
		ctx.nameObserver.destroy();
		ctx.iconObserver.destroy();
		ctx.resolver.destroy();
	}

	#updateRowSelectable(id: string) {
		const idx = this._tableRows.findIndex((r) => r.id === id);
		if (idx === -1) return;

		const row = this.#rows.get(id);
		const treeItem = this.#itemMap.get(id);
		const selectable = !(row?.currentNoAccess ?? false) && (treeItem ? this._isSelectableItem(treeItem) : false);

		if (this._tableRows[idx].selectable === selectable) return;

		this._tableRows = [
			...this._tableRows.slice(0, idx),
			{ ...this._tableRows[idx], selectable },
			...this._tableRows.slice(idx + 1),
		];
	}

	#updateRowHref(id: string, rows = this._tableRows): UmbTableItem[] | null {
		const idx = rows.findIndex((r) => r.id === id);
		if (idx === -1) return null;

		const row = this.#rows.get(id);
		const treeItem = this.#itemMap.get(id);
		if (!treeItem) return null;

		const href = this._selectable ? undefined : row?.currentPath || undefined;
		const nameData = rows[idx].data.find((d) => d.columnAlias === 'name');
		if (nameData?.value?.href === href) return null;

		const childrenIndicator = this.#getChildrenIndicator(treeItem, href);

		return [
			...rows.slice(0, idx),
			{
				...rows[idx],
				childrenIndicator,
				data: rows[idx].data.map((d) => (d.columnAlias === 'name' ? { ...d, value: { ...d.value, href } } : d)),
			},
			...rows.slice(idx + 1),
		];
	}

	#updateRowActive(id: string) {
		const idx = this._tableRows.findIndex((r) => r.id === id);
		if (idx === -1) return;

		const isActive = this.#rows.get(id)?.currentIsActive ?? false;
		if (this._tableRows[idx].active === isActive) return;

		this._tableRows = [
			...this._tableRows.slice(0, idx),
			{ ...this._tableRows[idx], active: isActive },
			...this._tableRows.slice(idx + 1),
		];
	}

	#updateRowName(id: string) {
		const idx = this._tableRows.findIndex((r) => r.id === id);
		if (idx === -1) return;

		const name = this.#resolveName(id);
		const nameData = this._tableRows[idx].data.find((d) => d.columnAlias === 'name');
		if (nameData?.value?.name === name) return;

		this._tableRows = [
			...this._tableRows.slice(0, idx),
			{
				...this._tableRows[idx],
				data: this._tableRows[idx].data.map((d) =>
					d.columnAlias === 'name' ? { ...d, value: { ...d.value, name } } : d,
				),
			},
			...this._tableRows.slice(idx + 1),
		];
	}

	#updateRowIcon(id: string) {
		const idx = this._tableRows.findIndex((r) => r.id === id);
		if (idx === -1) return;

		const icon = this.#resolveIcon(id);
		if (this._tableRows[idx].icon === icon) return;

		this._tableRows = [
			...this._tableRows.slice(0, idx),
			{ ...this._tableRows[idx], icon },
			...this._tableRows.slice(idx + 1),
		];
	}

	#resolveName(id: string): string {
		return this.#resolverContexts.get(id)?.currentName ?? this.#itemMap.get(id)?.name ?? '';
	}

	#resolveIcon(id: string): string {
		const item = this.#itemMap.get(id);
		const fallback = item?.isFolder ? 'icon-folder' : (item?.icon ?? getItemFallbackIcon());
		return this.#resolverContexts.get(id)?.currentIcon ?? fallback;
	}

	protected override _gotTreeContext() {
		super._gotTreeContext();

		this.observe(
			this._treeContext?.currentPageItems,
			(items) => {
				this._items = items ?? [];
				this.#createTableRows();
			},
			'_observeCurrentPageItems',
		);

		this.observe(
			this._treeContext?.hideTreeItemActions,
			(value) => (this._hideTreeItemActions = value ?? false),
			'_observeHideTreeItemActions',
		);
	}

	/**
	 * Whether the item shows an expand symbol, which leads further into the tree.
	 * @param {UmbTreeItemModel} item - The tree item the row represents.
	 * @returns {boolean} True when the item shows the expand symbol.
	 */
	protected _showExpandSymbol(item: UmbTreeItemModel): boolean {
		return item.hasChildren;
	}

	/**
	 * Where activating the expand symbol of the item leads. Without a path, it asks the tree to drill into the item.
	 * @param {UmbTreeItemModel} _item - The tree item the row represents.
	 * @param {string | undefined} path - The path of the item, when the row is not selectable.
	 * @returns {string | undefined} The path to navigate to.
	 */
	protected _getExpandPath(_item: UmbTreeItemModel, path: string | undefined): string | undefined {
		return path;
	}

	/**
	 * Renders the expand symbol of the item, or `undefined` for the default.
	 * @param {UmbTreeItemModel} _item - The tree item the row represents.
	 * @returns {TemplateResult | undefined} The symbol to render.
	 */
	protected _renderExpandSymbol(_item: UmbTreeItemModel): TemplateResult | undefined {
		return undefined;
	}

	#getChildrenIndicator(item: UmbTreeItemModel, path: string | undefined): UmbTableItem['childrenIndicator'] {
		if (!this._showExpandSymbol(item)) return undefined;

		return {
			href: this._getExpandPath(item, path),
			onOpen: () => this._treeContext?.open?.(item),
			renderExpandSymbol: () => this._renderExpandSymbol(item) ?? html`<uui-symbol-expand></uui-symbol-expand>`,
		};
	}

	#buildTableColumns(): Array<UmbTableColumn> {
		const nameColumn: UmbTableColumn = {
			name: this.localize.term('general_name'),
			alias: 'name',
			elementName: 'umb-tree-name-table-column-layout',
		};

		const manifestColumns: Array<UmbTableColumn> = this.#manifestColumns.map((col) => ({
			name: this.localize.string(col.label),
			alias: col.field,
		}));

		const entityActionsColumn: UmbTableColumn = {
			name: '',
			alias: 'entityActions',
			align: 'right',
			elementName: 'umb-entity-actions-table-column-view',
		};

		return [nameColumn, ...manifestColumns, ...(this._hideTreeItemActions ? [] : [entityActionsColumn])];
	}

	#toTableRow(item: UmbTreeItemModel): UmbTableItem {
		const id = item.unique;
		const row = this.#rows.get(id);
		const noAccess = row?.currentNoAccess ?? false;
		const href = this._selectable ? undefined : row?.currentPath || undefined;
		const isActive = row?.currentIsActive ?? false;
		const name = this.#resolveName(id);
		const icon = this.#resolveIcon(id);

		const manifestColumnData = this.#manifestColumns.map((col) => {
			const rawValue = (item as unknown as Record<string, unknown>)[col.field];
			if (col.valueType) {
				return {
					columnAlias: col.field,
					value: html`<umb-value-summary-extension
						.valueType=${col.valueType}
						.value=${rawValue}></umb-value-summary-extension>`,
				};
			}
			return { columnAlias: col.field, value: rawValue };
		});

		const childrenIndicator = this.#getChildrenIndicator(item, href);
		const onOpen = childrenIndicator?.onOpen;

		return {
			id,
			icon,
			entityType: item.entityType,
			childrenIndicator,
			selectable: !noAccess && this._isSelectableItem(item as UmbTreeItemModel),
			// select-only disables all row interaction, which would leave no way to drill into an item with
			// children while a selection is in progress.
			selectOnly: childrenIndicator ? false : undefined,
			active: isActive,
			data: [
				{
					columnAlias: 'name',
					value: {
						name,
						href,
						onOpen,
					},
				},
				...manifestColumnData,
				...(this._hideTreeItemActions ? [] : [{ columnAlias: 'entityActions', value: { name } }]),
			],
		};
	}

	#createTableRows() {
		const items = this._items;

		this.#itemMap.clear();
		for (const item of items) {
			this.#itemMap.set(item.unique, item);
		}

		// Resolve name/icon before building rows so they are present on first render.
		this.#syncResolverContexts();

		this._tableRows = items.map((item) => this.#toTableRow(item));

		const currentIds = new Set(this._tableRows.map((row) => row.id));
		for (const [id, row] of this.#rows) {
			if (!currentIds.has(id)) {
				row.destroy();
				this.#rows.delete(id);
			}
		}
	}

	override willUpdate(changedProperties: PropertyValues) {
		super.willUpdate(changedProperties);
		if (changedProperties.has('_selectable') || changedProperties.has('_selectOnly')) {
			this.#tableConfig = {
				allowSelection: this._selectable,
				allowSelectAll: false,
				selectOnly: this._selectOnly,
			};
		}

		if (changedProperties.has('_selectable')) {
			let rows = this._tableRows;
			for (const id of this.#itemMap.keys()) {
				rows = this.#updateRowHref(id, rows) ?? rows;
			}
			if (rows !== this._tableRows) this._tableRows = rows;
		}

		if (changedProperties.has('_hideTreeItemActions')) {
			this.#createTableRows();
		}
	}

	#onSelected(event: UmbTableSelectedEvent) {
		event.stopPropagation();
		const itemId = event.getItemId();
		if (itemId) this._selectItem(itemId);
	}

	#onDeselected(event: UmbTableDeselectedEvent) {
		event.stopPropagation();
		const itemId = event.getItemId();
		if (itemId) this._deselectItem(itemId);
	}

	override disconnectedCallback() {
		super.disconnectedCallback();
		for (const row of this.#rows.values()) {
			row.destroy();
		}
		this.#rows.clear();
		for (const ctx of this.#resolverContexts.values()) {
			this.#destroyResolverContext(ctx);
		}
		this.#resolverContexts.clear();
		this.#itemMap.clear();
	}

	override render() {
		if (!this._tableRows.length) return nothing;

		return html`
			<umb-table
				.config=${this.#tableConfig}
				.columns=${this.#buildTableColumns()}
				.items=${this._tableRows}
				.selection=${this._selection}
				.onRowRendered=${this.#onRowRendered}
				@selected=${this.#onSelected}
				@deselected=${this.#onDeselected}></umb-table>
			<umb-tree-pagination></umb-tree-pagination>
		`;
	}

	static override styles = [
		UmbTextStyles,
		css`
			:host {
				display: block;
			}
		`,
	];
}

export default UmbTableTreeViewElement;

declare global {
	interface HTMLElementTagNameMap {
		'umb-table-tree-view': UmbTableTreeViewElement;
	}
}
